// Admin-only 24-hour DPCA summary per patient.
// Aggregates all exchanges from all shifts of the selected date.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Loader2, Droplet, AlertTriangle, CheckCircle2, Calendar, User2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { APPEARANCE_LABELS, CONCENTRATION_LABELS } from "@/lib/dialysis-alerts";

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function ufBadge(uf, count) {
  if (!count) return { color: "text-slate-500", label: "Sin datos" };
  if (uf < -500) return { color: "text-red-700", label: "Sobrecarga (UF < -500 ml)" };
  if (uf > 1500) return { color: "text-amber-700", label: "UF alta (revisar hidratacion)" };
  if (uf >= 0 && uf <= 1500) return { color: "text-emerald-700", label: "Dentro de objetivo (0 a +1500 ml)" };
  return { color: "text-amber-700", label: "UF negativa moderada" };
}

export default function DialysisSummaryPage() {
  const nav = useNavigate();
  const [patients, setPatients] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/patients");
        const dpca = (Array.isArray(data) ? data : []).filter((p) => p.peritoneal_dialysis_active);
        setPatients(dpca);
        if (dpca.length && !selectedId) setSelectedId(dpca[0].id);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Error cargando pacientes");
        setPatients([]);
      }
    })();
  }, []);

  const loadSummary = useCallback(async () => {
    if (!selectedId || !date) {
      setSummary(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${selectedId}/dialysis-summary`, {
        params: { date },
      });
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error cargando resumen");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedId, date]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const selectedPatient = useMemo(
    () => (patients || []).find((p) => p.id === selectedId),
    [patients, selectedId]
  );

  const uf = summary?.ultrafiltration_ml ?? 0;
  const badge = ufBadge(uf, summary?.exchanges_count || 0);

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AppHeader subtitle="Resumen 24h - Dialisis Peritoneal" />

      <main className="mx-auto max-w-3xl px-4 py-6">
        <Button
          variant="ghost"
          className="mb-3 h-10 px-2 text-slate-600"
          onClick={() => nav("/dashboard")}
          data-testid="dialysis-back-btn"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Volver
        </Button>

        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h1 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
            Balance 24h de Ultrafiltracion
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Agrupa todos los recambios registrados por cualquier enfermera para este paciente en el dia seleccionado.
          </p>
        </div>

        {patients === null && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        )}

        {patients && patients.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-10 text-center">
            <Droplet className="mx-auto mb-2 h-8 w-8 text-slate-300" />
            <p className="font-semibold text-slate-700">Sin pacientes en DPCA</p>
            <p className="mt-1 text-sm text-slate-500">
              Activa la Dialisis Peritoneal en la ficha del paciente para que aparezca aqui.
            </p>
          </div>
        )}

        {patients && patients.length > 0 && (
          <>
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="mb-1 flex items-center gap-1 text-xs font-bold uppercase text-slate-500">
                  <User2 className="h-3 w-3" /> Paciente
                </Label>
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger className="h-11 text-base" data-testid="dialysis-summary-patient">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 flex items-center gap-1 text-xs font-bold uppercase text-slate-500">
                  <Calendar className="h-3 w-3" /> Fecha
                </Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="h-11 text-base"
                  data-testid="dialysis-summary-date"
                />
              </div>
            </div>

            {loading && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            )}

            {!loading && summary && (
              <div className="space-y-4">
                {/* Totals cards */}
                <div
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  data-testid="dialysis-summary-totals"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {selectedPatient?.name} - {summary.date}
                      </p>
                      <p className="mt-1 text-lg font-bold text-slate-900">
                        {summary.exchanges_count} recambios · {summary.per_shift.length} turnos
                      </p>
                    </div>
                    <div
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                        badge.color === "text-red-700"
                          ? "bg-red-100 text-red-800"
                          : badge.color === "text-emerald-700"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                      data-testid="dialysis-summary-badge"
                    >
                      {badge.color === "text-emerald-700" ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <AlertTriangle className="h-3.5 w-3.5" />
                      )}
                      {badge.label}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-xl bg-slate-50 p-3">
                      <div className="text-[10px] font-bold uppercase text-slate-500">Peso matutino</div>
                      <div className="mt-1 font-mono text-lg font-bold text-slate-900">
                        {summary.morning_weight_kg || "--"} <span className="text-xs font-medium">kg</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-blue-50 p-3">
                      <div className="text-[10px] font-bold uppercase text-blue-700">Infundido</div>
                      <div className="mt-1 font-mono text-lg font-bold text-blue-900">
                        {summary.total_infused_ml} <span className="text-xs font-medium">ml</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-cyan-50 p-3">
                      <div className="text-[10px] font-bold uppercase text-cyan-700">Drenado</div>
                      <div className="mt-1 font-mono text-lg font-bold text-cyan-900">
                        {summary.total_drained_ml} <span className="text-xs font-medium">ml</span>
                      </div>
                    </div>
                    <div
                      className={`rounded-xl p-3 ${
                        uf < 0 ? "bg-red-50" : "bg-emerald-50"
                      }`}
                    >
                      <div
                        className={`text-[10px] font-bold uppercase ${
                          uf < 0 ? "text-red-700" : "text-emerald-700"
                        }`}
                      >
                        UF Balance
                      </div>
                      <div
                        className={`mt-1 font-mono text-lg font-bold ${
                          uf < 0 ? "text-red-900" : "text-emerald-900"
                        }`}
                        data-testid="dialysis-summary-uf"
                      >
                        {uf > 0 ? "+" : ""}{uf} <span className="text-xs font-medium">ml</span>
                      </div>
                    </div>
                  </div>

                  {summary.non_clear_count > 0 && (
                    <div
                      className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-medium text-red-800"
                      data-testid="dialysis-summary-alert"
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                      <span>
                        {summary.non_clear_count} recambio(s) con liquido no claro (posible peritonitis). Revisar y notificar al medico tratante.
                      </span>
                    </div>
                  )}
                </div>

                {/* Per-shift breakdown */}
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                    Detalle por turno
                  </p>
                  {summary.per_shift.length === 0 ? (
                    <p className="py-4 text-center text-sm text-slate-500">
                      Sin recambios registrados en este dia.
                    </p>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {summary.per_shift.map((s, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 py-2 text-sm">
                          <div>
                            <div className="font-semibold text-slate-900">
                              {s.shift_start || "--:--"} · {s.owner_name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {s.exchanges_count} recambio(s)
                            </div>
                          </div>
                          <div className="text-right font-mono">
                            <div className="text-sm text-slate-700">
                              {s.infused_ml} / {s.drained_ml} ml
                            </div>
                            <div
                              className={`text-xs font-bold ${
                                s.ultrafiltration_ml < 0 ? "text-red-700" : "text-emerald-700"
                              }`}
                            >
                              UF {s.ultrafiltration_ml > 0 ? "+" : ""}{s.ultrafiltration_ml} ml
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Non-clear detail if any */}
                {summary.non_clear.length > 0 && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-4 shadow-sm">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-red-800">
                      <AlertTriangle className="h-3.5 w-3.5" /> Recambios con liquido no-claro
                    </p>
                    <ul className="space-y-1 text-sm text-red-900">
                      {summary.non_clear.map((e, i) => (
                        <li key={i}>
                          <span className="font-mono font-bold">
                            #{e.number || "?"} · {e.start_time || "--:--"}
                          </span>{" "}
                          — {APPEARANCE_LABELS[e.appearance] || e.appearance}{" "}
                          <span className="text-xs text-red-700">
                            (bolsa {CONCENTRATION_LABELS[e.concentration] || e.concentration})
                          </span>
                          {e.notes && <span className="ml-1 italic text-red-700">— {e.notes}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
